import React from 'react';
import {
    Eyebrow,
    EyebrowProps,
    ImageProps,
    Image,
    CenteredGrid,
    VerticalSpacer,
    Typography,
    HiddenSSR,
    CmsButtonProps,
    Hyperlink,
    Button,
    LightboxButtonProps,
    PlayIcon
} from '../../elements';
import {WithCaption, WithCaptionProps} from '../../wraps';
import Grid from '@material-ui/core/Grid';
import styled from 'styled-components';
import {AccentColorOptions} from 'epic-ue-theme';
import {BackgroundVideoOptionsArray} from '../../../types';

const Slide = styled.div`
    margin: 0 auto;
    max-height: 588px;

    .image-wrapper {
        max-height: 588px;
    }

    .slide-desc {
        .paragraph {
            color: ${(p) => p.theme.palette.text.secondary};

            ${(p) => p.theme.breakpoints.down('md')} {
                line-height: 26px;
            }
        }
    }

    .hlink-wrap {
        display: block;
    }

    .ue-centered-grid {
        width: unset;
        margin: unset;
    }
`;

export interface FeaturesCarouselSlideProps {
    slideName?: string;
    key?: string;
    title?: string;
    description?: string;
    image?: ImageProps;
    eyebrow?: EyebrowProps;
    caption?: WithCaptionProps;
    links?: CmsButtonProps[];
    accentColor?: AccentColorOptions;
    activeSlide?: boolean;
    noTopSpacing?: boolean;
    lightboxButton?: LightboxButtonProps;
    toggleLightbox?: (lightboxOptions: BackgroundVideoOptionsArray) => void;
}

export const FeaturesCarouselSlide = ({
    title,
    description,
    eyebrow,
    caption,
    image,
    slideName,
    links = [],
    accentColor,
    activeSlide,
    noTopSpacing = false,
    lightboxButton,
    toggleLightbox
}: FeaturesCarouselSlideProps): JSX.Element => {
    const half = Math.ceil(links.length / 2);

    let firstHalf = links;
    let secondHalf: CmsButtonProps[] = [];
    if (links.length > 10) {
        firstHalf = links.slice(0, half);
        secondHalf = links.slice(-half + 1);
    }

    // the icon doesn't work with the primary type
    let lightboxButtonType = lightboxButton?.type || 'tertiary';
    if (lightboxButton?.type === 'primary' && lightboxButton?.showPlayIcon) {
        lightboxButtonType = 'secondary';
    }

    const imageLayout = (
        <>
            <Grid item xs={6} md={7} style={{paddingBottom: 0, paddingRight: 0}}>
                <WithCaption {...caption}>
                    <Image {...image} ratio="square" optMaxWidth={900} />
                </WithCaption>
            </Grid>
            <Grid item xs={1} />
            <Grid item xs={5} md={4} style={{paddingBottom: 0, paddingLeft: 0}}>
                {!noTopSpacing && (
                    <HiddenSSR mdDown>
                        <VerticalSpacer variant="custom" spacing={5} />
                    </HiddenSSR>
                )}
                <Typography variant="h2" as="h3">
                    {title}
                </Typography>
                <VerticalSpacer variant="custom" spacing={3} downSmSpacing={1} />
                <div className="slide-desc">
                    <Typography variant="p">{description}</Typography>
                </div>
                <VerticalSpacer variant="custom" spacing={5} downSmSpacing={4} />

                {eyebrow?.text && <Eyebrow {...eyebrow} />}
                {eyebrow?.text && <VerticalSpacer variant="custom" spacing={2} downSmSpacing={1} />}
                {lightboxButton?.label ? (
                    <Button
                        type={lightboxButtonType}
                        startIcon={lightboxButton?.showPlayIcon ? <PlayIcon /> : null}
                        color={accentColor}
                        onClick={() => {
                            toggleLightbox && toggleLightbox(lightboxButton?.mediaOptions);
                        }}>
                        {lightboxButton?.label}
                    </Button>
                ) : (
                    <div className="explore-links">
                        {links.map((link, i) => (
                            <React.Fragment key={`fc-explore-link-${i}-${link.label}`}>
                                <Typography variant="p">
                                    <Button
                                        type="tertiary"
                                        tabIndex={activeSlide ? 0 : -1}
                                        href={link.href}
                                        color={accentColor}>
                                        {link.label}
                                    </Button>
                                </Typography>
                                {i < links.length - 1 ? (
                                    <HiddenSSR mdDown>
                                        <VerticalSpacer variant="paragraph" />
                                    </HiddenSSR>
                                ) : (
                                    ''
                                )}
                            </React.Fragment>
                        ))}
                    </div>
                )}
            </Grid>
        </>
    );

    const exploreLayout = (
        <>
            <Grid item xs={1} />
            <Grid item xs={10} style={{paddingBottom: 0}}>
                <CenteredGrid alignItems="flex-start">
                    <Grid item xs={12}>
                        <VerticalSpacer variant="custom" spacing={5} downSmSpacing={4} />
                        <Eyebrow {...eyebrow} />
                        <VerticalSpacer variant="custom" spacing={2} downSmSpacing={1} />
                    </Grid>

                    <Grid item xs={6} style={{paddingBottom: 0}}>
                        {firstHalf.map((link, i) => (
                            <React.Fragment key={`fc-link-first-${i}-${link.label}`}>
                                <Typography variant="h3">
                                    <Hyperlink
                                        href={link.href}
                                        accentColor={accentColor}
                                        tabIndex={activeSlide ? 0 : -1}>
                                        {link.label}
                                    </Hyperlink>
                                </Typography>
                                {i < links.length - 1 ? <VerticalSpacer variant="paragraph" /> : ''}
                            </React.Fragment>
                        ))}
                    </Grid>
                    <Grid item xs={6} style={{paddingBottom: 0}}>
                        {secondHalf.map((link, i) => (
                            <React.Fragment key={`fc-link-second-${i}-${link.label}`}>
                                <Typography variant="h3">
                                    <Hyperlink
                                        href={link.href}
                                        accentColor={accentColor}
                                        tabIndex={activeSlide ? 0 : -1}>
                                        {link.label}
                                    </Hyperlink>
                                </Typography>
                                {i < links.length - 1 ? <VerticalSpacer variant="paragraph" /> : ''}
                            </React.Fragment>
                        ))}
                    </Grid>
                </CenteredGrid>
            </Grid>
            <Grid item xs={1} />
        </>
    );

    return (
        <Slide key={slideName}>
            <CenteredGrid alignItems="flex-start">
                {image && image.src && title ? imageLayout : exploreLayout}
            </CenteredGrid>
        </Slide>
    );
};
